/**
 * The Patient Profile page's and the Schedule View patient panel's Tasks tab — the pure rules
 * (Patient Profile overhaul, 2026-10-09: the tab draws the Tasks page's own rows).
 *
 * The tab (src/components/PatientPanel/tabs/TasksTab.vue) draws TaskRow / TasksSection from the Tasks
 * page (src/pages/TaskList/components/) and opens its TaskDrawer. This file holds what the tab
 * decides, so it can be tested without a component:
 *
 *   • THE TASK SHAPE needs NO mapping. state.tasks.tasksTaggedWithPatient (store/tasks.js
 *     getTasksTaggedWithPatient) formats every task exactly as the Tasks page's own lists do —
 *     createdAt 'MM/DD/YYYY h:mm:ss a z', dueAt 'MM/DD/YYYY h:mm:ss a', completedAt the same, `id` set —
 *     and myDay.js rowMeta reads 'M/D/YYYY h:mm:ss a' as the calendar date it spells (parseUs). So the
 *     store's objects go to TaskRow as they are; nothing here copies or mutates one.
 *   • THE ORDER of the Open rows is the old Tasks card's: soonest due first (by the exact time), tasks
 *     with no readable date last.
 *   • THE ctx every TaskRow needs (myDay.js): who is signed in, today as a 'YYYY-MM-DD' in the SITE's
 *     timezone, the zone, and the DOM-exact content cleaner.
 *   • THE DRAWER's width and its site-tag list, as ToDo.vue's tkOpen builds them.
 *
 * ⛔ Reads no store and no DOM except through what is passed in.
 */
import moment from 'moment-timezone';
import { DEFAULT_TIME_ZONE, groupList } from '@/utils/tasks/myDay.js';
import { cleanTaskContent } from '@/utils/tasks/completeTask.js';

/** How the tasks store formats dueAt — for the tagged-with-patient slot AND the Tasks page's lists. */
export const DUE_FORMAT = 'MM/DD/YYYY h:mm:ss a';

/**
 * The Tasks page's task drawer width (ToDo.vue TK_DRAWER_WIDTH). Pinned equal by
 * tests/component/PatientTasksTab.spec.js ("SOURCE").
 */
export const TASK_DRAWER_WIDTH = 540;

const YMD = /^\d{4}-\d{2}-\d{2}$/;

function dueMoment(task) {
  return task && task.dueAt ? moment(task.dueAt, DUE_FORMAT) : moment.invalid();
}

/** Open tasks, soonest due first, undated (or unreadable) last; ties keep their input order. */
export function sortOpenByDue(tasks) {
  return (Array.isArray(tasks) ? tasks : []).slice().sort((a, b) => {
    const da = dueMoment(a);
    const db = dueMoment(b);
    const va = da.isValid();
    const vb = db.isValid();
    if (!va && !vb) return 0;
    if (!va) return 1;
    if (!vb) return -1;
    return da.valueOf() - db.valueOf();
  });
}

/**
 * { open, completed } from the patient's tasks. `open` = everything not COMPLETED, in the order above;
 * `completed` = the COMPLETED ones, newest first by the Tasks page's own comparator (myDay.js groupList).
 * The input objects are returned as they are.
 */
export function splitPatientTasks(tasks, ctx) {
  const list = (Array.isArray(tasks) ? tasks : []).filter(t => t && typeof t === 'object');
  return {
    open: sortOpenByDue(list.filter(t => t.status !== 'COMPLETED')),
    completed: groupList(list.filter(t => t.status === 'COMPLETED'), ctx).done,
  };
}

/** A valid timezone name, else the Tasks page's default (America/New_York). */
export function validZone(timezone) {
  return typeof timezone === 'string' && timezone && moment.tz.zone(timezone) ? timezone : DEFAULT_TIME_ZONE;
}

/**
 * Today as 'YYYY-MM-DD' at the site: the host's own `today` when it is one, else now in the site's
 * timezone, else now in moment's default zone.
 */
export function resolveToday(today, timezone, now) {
  if (typeof today === 'string' && YMD.test(today)) return today;
  const m = now ? moment(now) : moment();
  return typeof timezone === 'string' && timezone && moment.tz.zone(timezone)
    ? m.tz(timezone).format('YYYY-MM-DD')
    : m.format('YYYY-MM-DD');
}

/** myDay.js ctx for TaskRow / TasksSection. `now` is for tests. */
export function buildTaskCtx({ userID, today, timezone, now } = {}) {
  return {
    userID: userID === undefined ? null : userID,
    today: resolveToday(today, timezone, now),
    timeZone: validZone(timezone),
    clean: content => cleanTaskContent(content, typeof document !== 'undefined' ? document : null),
  };
}

/**
 * The drawer's `siteTags`: ToDo.vue computedAvailableTags with THIS patient's tasks — the site's tag
 * list (state.tasks.siteTaskTags) plus the tags the tasks carry, trimmed, unique, sorted.
 */
export function drawerSiteTags(siteTaskTags, tasks) {
  const unique = new Set();
  const add = (tag) => {
    if (typeof tag === 'string' && tag.trim() !== '') unique.add(tag.trim());
  };
  (Array.isArray(siteTaskTags) ? siteTaskTags : []).forEach(add);
  (Array.isArray(tasks) ? tasks : []).forEach((t) => {
    if (t && Array.isArray(t.tags)) t.tags.forEach(add);
  });
  return Array.from(unique).sort();
}
